import { DOCUMENT } from '@angular/common';
import { inject, APP_ID } from '@angular/core';

export function escapeHtml(text: string): string {
    const escapedText: { [k: string]: string } = {
        '&': '&a;',
        '"': '&q;',
        "'": '&s;',
        '<': '&l;',
        '>': '&g;',
    };
    return text.replace(/[&"'<>]/g, (s) => escapedText[s]);
}

export function unescapeHtml(text: string): string {
    const unescapedText: { [k: string]: string } = {
        '&a;': '&',
        '&q;': '"',
        '&s;': "'",
        '&l;': '<',
        '&g;': '>',
    };
    return text.replace(/&[^;]+;/g, (s) => unescapedText[s]);
}

export function retrieveTransferredState(doc: Document, appId: string) {
    // Locate the script tag with the JSON data transferred from the server.
    // The id of the script tag is set to the Angular appId + 'state'.
    const script = doc.getElementById(appId + '-state');
    let initialState = {};
    if (script && script.textContent) {
        try {
            initialState = JSON.parse(script.textContent) as {};
        } catch (e) {
            initialState = JSON.parse(unescapeHtml(script.textContent)) as {};
        }
    }
    return initialState;
}

export class CustomTransferState {
    store: { [k: string]: unknown | undefined } = {};
    onSerializeCallbacks = {};

    constructor() {
        this.store = retrieveTransferredState(inject(DOCUMENT), inject(APP_ID));
        this.onSerializeCallbacks = {};
    }
    /**
     * Get the value corresponding to a key. Return `defaultValue` if key is not found.
     */
    get(key, defaultValue) {
        return this.store[key] !== undefined ? this.store[key] : defaultValue;
    }
    /**
     * Set the value corresponding to a key.
     */
    set(key, value) {
        this.store[key] = value;
    }
    /**
     * Remove a key from the store.
     */
    remove(key) {
        delete this.store[key];
    }
    /**
     * Test whether a key exists in the store.
     */
    hasKey(key) {
        return this.store.hasOwnProperty(key);
    }
    /**
     * Indicates whether the state is empty.
     */
    get isEmpty() {
        return Object.keys(this.store).length === 0;
    }
    /**
     * Register a callback to provide the value for a key when `toJson` is called.
     */
    onSerialize(key, callback) {
        this.onSerializeCallbacks[key] = callback;
    }
    /**
     * Serialize the current state of the store to JSON.
     */
    toJson() {
        // Call the onSerialize callbacks and put those values into the store.
        for (const key in this.onSerializeCallbacks) {
            if (this.onSerializeCallbacks.hasOwnProperty(key)) {
                try {
                    this.store[key] = this.onSerializeCallbacks[key]();
                } catch (e) {
                    console.warn('Exception in onSerialize callback: ', e);
                }
            }
        }
        // Escape script tag to avoid break out of <script> tag in serialized output.
        // Encoding of `<` is the same behaviour as G3 script_builders.
        return JSON.stringify(this.store).replace(/</g, '\\u003C');
    }
}
